import { CopyOutlined } from "@ant-design/icons";
import { Button, Table } from "@shared/ui";
import type { ColumnsType } from "antd/es/table";

import { IBriefLedger, LEDGER_TYPE } from "@shared/lib/types/briefLedger";
import styles from "./BillingSummaryTable.module.scss";

export interface BillingSummaryRow {
  id: string;
  creator: string;
  campaign: string;
  purchase: number;
  service: number;
  gst: number;
  total: number;
  isNegative: boolean;
}

interface BillingSummaryTableProps {
  purchases: IBriefLedger[];

  totalPurchaseCost: number;
  totalSellingCost: number;
  totalServiceCost: number;
  totalGst: number;
  grandTotal: number;
  onCopySummary: () => void;
}

export function BillingSummaryTable({
  purchases,
  totalPurchaseCost,
  totalSellingCost,
  grandTotal,
  onCopySummary,
  totalGst,
  totalServiceCost,
}: BillingSummaryTableProps) {
  const dataSource: BillingSummaryRow[] = purchases.map((p) => {
    return {
      id: p._id,
      creator: p?.socialMediaName || p?.category || "",
      campaign: p.brief?.name || "",
      purchase: p.baseCost,
      service: p.serviceCost,
      gst: p.gstAmount,
      total: p.totalAmount,
      sellingCost: p.sellingCost,
      isNegative: p.type === LEDGER_TYPE.Credit,
    };
  });

  const columns: ColumnsType<BillingSummaryRow> = [
    {
      title: "Creator",
      dataIndex: "creator",
      key: "creator",
      render: (val: string) => (
        <span className={styles.cellCreator}>{val}</span>
      ),
    },
    {
      title: "Campaign",
      dataIndex: "campaign",
      key: "campaign",
      render: (val: string) => (
        <span className={styles.cellCampaign}>{val}</span>
      ),
    },
    {
      title: "Purchase",
      dataIndex: "sellingCost",
      key: "purchase",
      align: "right",
      render: (val: number, record: BillingSummaryRow) => (
        <span
          className={`${styles.cellAmount} ${record.isNegative ? styles.amountNegative : ""}`}
        >
          {record.isNegative ? "-" : ""}₹{val.toLocaleString()}
        </span>
      ),
    },
    {
      title: "Service",
      dataIndex: "service",
      key: "service",
      align: "right",
      render: (val: number, record: BillingSummaryRow) => (
        <span
          className={`${styles.cellAmount} ${record.isNegative ? styles.amountNegative : ""}`}
        >
          {record.isNegative ? "-" : ""}₹{val.toLocaleString()}
        </span>
      ),
    },
    {
      title: "GST",
      dataIndex: "gst",
      key: "gst",
      align: "right",
      render: (val: number, record: BillingSummaryRow) => (
        <span
          className={`${styles.cellAmount} ${record.isNegative ? styles.amountNegative : ""}`}
        >
          {record.isNegative ? "-" : ""}₹{val.toLocaleString()}
        </span>
      ),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (val: number, record: BillingSummaryRow) => (
        <span
          className={`${styles.cellTotal} ${record.isNegative ? styles.amountNegative : ""}`}
        >
          {record.isNegative ? "-" : ""}₹{val.toLocaleString()}
        </span>
      ),
    },
  ];

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <h4 className={styles.title}>Billing Summary</h4>
        <Button
          type="default"
          size="small"
          icon={<CopyOutlined />}
          onClick={onCopySummary}
          className={styles.copyBtn}
        >
          Copy Table
        </Button>
      </div>
      <div className={styles.tableWrap}>
        <Table<BillingSummaryRow>
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          showHeader
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <span className={styles.footerLabel}>Total</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  ₹{totalSellingCost.toLocaleString()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  ₹{totalServiceCost.toLocaleString()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  ₹{totalGst.toLocaleString()}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <span className={styles.footerGrand}>
                    ₹{grandTotal.toLocaleString()}
                  </span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </div>
    </div>
  );
}
